
'use client';

import { useState } from 'react';
import type { DataGridProps } from '../types';

interface ListViewProps extends DataGridProps {
  titleColumn?: string;
  visibleColumns?: string[];
  layout?: {
    cardHeight?: string;
    compactMode?: boolean;
    showActions?: boolean;
  };
  onTitleColumnChange?: (columnField: string) => void;
  onViewChange?: (view: string) => void;
}

export const ListView: React.FC<ListViewProps> = ({ dataSource, columns, gridConfig, onRowClick, titleColumn, visibleColumns: propVisibleColumns, layout, onTitleColumnChange, onViewChange }) => {
  // Use prop visible columns if provided, otherwise filter from columns
  const visibleColumns = propVisibleColumns && propVisibleColumns.length > 0
    ? columns.filter(col => propVisibleColumns.includes(col.field || col.columnName))
    : columns.filter(col => col.visible !== false && col.field);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  // Find the title column or use the first visible column as fallback
  const titleColumnConfig = titleColumn
    ? visibleColumns.find(col => col.field === titleColumn)
    : visibleColumns[0];

  // Filter columns for dropdown (only text columns suitable for titles)
  const textColumns = visibleColumns.filter(col =>
    col.field && (col.columnType === 'text' || !col.columnType)
  );

  // Determine how many columns to show by default based on screen size
  const defaultVisibleCount = 5;
  const hasMoreColumns = visibleColumns.length > defaultVisibleCount;

  const toggleItemExpansion = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const renderCellContent = (value: any, column: any) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-gray-400 italic">—</span>;
    }

    switch (column.columnType) {
      case 'currency':
        // INR by default — see the note in hooks/useDataGrid.tsx. 'DOLLAR' is opt-in.
        const symbol = column.displayFormat === 'DOLLAR' ? '$' : '₹';
        const locale = column.displayFormat === 'DOLLAR' ? 'en-US' : 'en-IN';
        return (
          <span className="font-semibold text-green-600">
            {symbol}{value?.toLocaleString(locale)}
          </span>
        );
      case 'rating':
        return (
          <div className="flex items-center">
            <div className="flex text-yellow-400 mr-2">
              {[...Array(5)].map((_, i) => (
                <span key={i} className={i < value ? 'text-yellow-400' : 'text-gray-300'}>
                  ★
                </span>
              ))}
            </div>
            <span className="text-sm text-gray-600">({value}/5)</span>
          </div>
        );
      case 'percentage':
        return <span className="font-medium text-violet-600">{value}%</span>;
      case 'boolean':
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
            }`}>
            <i className={`mr-1 w-3 h-3 ${value ? 'ri-check-line' : 'ri-close-line'
              }`}></i>
            {value ? 'Yes' : 'No'}
          </span>
        );
      case 'date':
        return value ? (
          <span className="text-gray-700">
            {new Date(value).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </span>
        ) : null;
      default:
        return <span className="text-gray-800">{value}</span>;
    }
  };

  return (
    <div className={`space-y-4 ${gridConfig.views?.vw_css_class || ''}`}>
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Back Button */}
        {/* <div className="mb-4">
          <button
            onClick={() => onViewChange?.('table')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors duration-200 shadow-sm"
          >
            <i className="ri-arrow-left-line mr-2 w-4 h-4"></i>
            Back to Table View
          </button>
        </div> */}

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">List View</h2>
            <p className="text-gray-600">
              {titleColumnConfig ? (
                <>Showing <span className="font-medium">{dataSource.length}</span> items grouped by <span className="font-medium text-violet-600">{titleColumnConfig.columnName}</span></>
              ) : (
                <>Showing <span className="font-medium">{dataSource.length}</span> items</>
              )}
              {hasMoreColumns && (
                <span className="ml-2 text-sm">
                  • <span className="font-medium">{visibleColumns.length}</span> columns available
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Column Chooser Dropdown */}
            {onTitleColumnChange && textColumns.length > 0 && (
              <div className="relative">
                <div className="flex items-center space-x-3 bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 shadow-sm hover:bg-gray-100 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                      <i className="ri-layout-column-line w-4 h-4 text-violet-600"></i>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Title Column</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {titleColumnConfig?.columnName || 'Select Column'}
                      </div>
                    </div>
                  </div>
                  <div className="relative">
                    <select
                      value={titleColumn || ''}
                      onChange={(e) => onTitleColumnChange(e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    >
                      {textColumns.map((column) => (
                        <option key={column.field} value={column.field}>
                          {column.columnName}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line w-5 h-5 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>
              </div>
            )}
            <div className="text-sm text-gray-500 flex items-center">
              <i className="ri-layout-row-line mr-1 w-4 h-4"></i>
              Professional Layout
            </div>
          </div>
        </div>
      </div>

      {/* List Items */}
      {dataSource.map((item, index) => {
        const isExpanded = expandedItems.has(index);
        const visibleFields = isExpanded ? visibleColumns : visibleColumns.slice(0, defaultVisibleCount);
        const hiddenFieldsCount = visibleColumns.length - defaultVisibleCount;

        return (
          <div
            key={index}
            className={`bg-white border border-gray-200 rounded-xl hover:shadow-lg focus:shadow-lg transition-all duration-300 cursor-pointer group overflow-hidden ${isExpanded ? 'shadow-md border-violet-200' : 'shadow-sm'
              }`}
            onClick={() => onRowClick?.(item)}
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && onRowClick?.(item)}
          >
            {/* Header Section */}
            <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                {/* Primary Title */}
                {titleColumnConfig && (
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-violet-600 transition-colors truncate">
                      {item[titleColumnConfig.field!] || '—'}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 flex items-center">
                      <i className="ri-layout-column-line mr-1 w-4 h-4"></i>
                      {titleColumnConfig.columnName}
                    </p>
                  </div>
                )}

                {/* Actions and Expand Button */}
                <div className="flex items-center space-x-3">
                  {/* Action Buttons */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRowClick?.(item);
                    }}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors duration-200"
                  >
                    <i className="ri-eye-line mr-1 w-4 h-4"></i>
                    View
                  </button>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors duration-200"
                  >
                    <i className="ri-edit-line mr-1 w-4 h-4"></i>
                    Edit
                  </button>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                  >
                    <i className="ri-more-2-line w-4 h-4"></i>
                  </button>

                  {/* Expand Button */}
                  {hasMoreColumns && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleItemExpansion(index);
                      }}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-200"
                    >
                      <i className={`mr-1 w-4 h-4 transition-transform duration-200 ${isExpanded ? 'ri-arrow-up-line rotate-0' : 'ri-arrow-down-line'
                        }`}></i>
                      {isExpanded ? 'Less' : `+${hiddenFieldsCount}`}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-6">
              {/* Main Fields - 4 columns per row */}
              <div className="space-y-6">
                {(() => {
                  const fieldsToShow = visibleFields.filter(column => column.field !== titleColumnConfig?.field);
                  const rows = [];

                  // Group fields into rows of 4
                  for (let i = 0; i < fieldsToShow.length; i += 4) {
                    const rowFields = fieldsToShow.slice(i, i + 4);
                    rows.push(
                      <div key={`row-${i}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {rowFields.map((column) => (
                          <div key={column.field || column.columnName} className="space-y-1">
                            {/* Value on top */}
                            <div className="text-base font-semibold text-gray-900 truncate">
                              {renderCellContent(item[column.field!], column)}
                            </div>
                            {/* Label on bottom */}
                            <div className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                              <i className="ri-information-line mr-1 w-3 h-3"></i>
                              <span className="truncate">{column.columnName}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }

                  return rows;
                })()}
              </div>
            </div>
          </div>
        );
      })}

      {/* Empty State */}
      {dataSource.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <i className="ri-inbox-line w-8 h-8 text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No Items Found</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              There are no items to display in this view. Try adjusting your filters or adding new data.
            </p>
            <div className="flex items-center justify-center space-x-4">
              <button className="inline-flex items-center px-4 py-2 text-sm font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors duration-200">
                <i className="ri-add-line mr-2 w-4 h-4"></i>
                Add New Item
              </button>
              <button className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                <i className="ri-refresh-line mr-2 w-4 h-4"></i>
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
